import React, { useCallback, useContext, useMemo, useState } from 'react';
import { LocalizationContext } from 'app/localization/Context';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import CustomIconBack from 'app/home/home-components/CustomIconBack';
import { RoomIcon } from 'app/home/home-components/room-header-tab/RoomHeaderTab';
import { getDeviceColorsRGB } from 'app/model/Device';

import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as IconRearrange } from 'images/setting/rearrange.svg';
import { ReactComponent as IconDownBoxed } from 'images/general/down-boxed.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';
import { ReactComponent as IconPlus } from 'images/general/icon-add.svg';
import { ReactComponent as IconSelection } from 'images/general/icon-badge-select.svg';


import './Rooms.scss'

export default function Rooms({ dataFetched, roomsData, roomsController, currentUser }) {
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);
    const dismissHandler = p.dismiss;

    const l = useContext(LocalizationContext);
    const s = l.strings;
    const r = s.rooms;

    const [tempRoomsOrder, setTempRoomsOrder] = useState(undefined); // roomsData.roomsOrder

    const _roomsOrder = useMemo(() => {
        if (tempRoomsOrder !== undefined) {
            return tempRoomsOrder;
        } else {
            return roomsData.roomsOrder;
        }
    }, [roomsData.roomsOrder, tempRoomsOrder]);

    const roomTempUp = useCallback((index) => {
        if (index > 0) {
            const newOrder = [...tempRoomsOrder];
            let x = newOrder[index - 1];
            newOrder[index - 1] = newOrder[index];
            newOrder[index] = x;
            setTempRoomsOrder(newOrder);
        }
    }, [tempRoomsOrder]);

    const roomTempDown = useCallback((index) => {
        if (index < tempRoomsOrder.length - 1) {
            const newOrder = [...tempRoomsOrder];
            let x = newOrder[index + 1];
            newOrder[index + 1] = newOrder[index];
            newOrder[index] = x;
            setTempRoomsOrder(newOrder);
        }
    }, [tempRoomsOrder]);

    const saveRoomOrder = useCallback(() => {
        roomsController.changeOrder(tempRoomsOrder);
        setTempRoomsOrder(undefined);
    }, [tempRoomsOrder, roomsController]);

    const roomList = useMemo(() => {
        const roomIds = Object.keys(roomsData.rooms);
        roomIds.sort();
        return roomIds.map(id => roomsData.rooms[id]);
    }, [roomsData.rooms]);

    const settingPermission = currentUser.permissions.setting;
    const inRoomReordering = (tempRoomsOrder !== undefined);


    const goToDetail = useCallback((roomId) => {
        if (!inRoomReordering) {
            hs.push("/settings/rooms_management/detail", { selectedRoomId: roomId })
        }
    }, [inRoomReordering, hs]);

    const openAddRoomPages = useCallback(() => {
        hs.push("/settings/rooms_management/add")
    }, [hs]);

    return (
        <div className="rooms-management-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="rooms-management-page-content">
                <div className="nav-button" onClick={dismissHandler} />
                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    {
                        ((roomsData.roomsOrder.length > 0) && settingPermission) ? (
                            <div className={"button-border " + (inRoomReordering ? "disable" : "")}
                                onClick={() => { if (!inRoomReordering) setTempRoomsOrder(roomsData.roomsOrder) }}>
                                <div className="label">{r.rearrange}</div>
                                <IconRearrange className="icon" />
                            </div>
                        ) : null
                    }
                    {
                        ((roomList.length > 0) && settingPermission) ? (
                            <div className={"button-border " + (inRoomReordering ? "disable" : "")}
                                onClick={() => { if (!inRoomReordering) openAddRoomPages() }}>
                                <div className="label">{r.add}</div>
                                <IconPlus className="icon" />
                            </div>
                        ) : null
                    }
                </div>
                <div className="page-header">{r.roomManagement}</div>

                <div className="room-list with-margin-top">
                    {
                        roomList.map(room => {
                            const index = _roomsOrder.indexOf(room.room_id);
                            const noColorLine = getDeviceColorsRGB(room.color) === "#000000";

                            return (
                                <div
                                    key={"rooms-management-cell-room-id-" + String(room.room_id)}
                                    className="room" style={{ top: index * (75 + 8) }}
                                    onClick={() => { goToDetail(room.room_id) }}>

                                    <RoomIcon className="icon" avatar={room.avatar} />
                                    {noColorLine ? null : (
                                        <div className="color-line" style={{ backgroundColor: getDeviceColorsRGB(room.color) }} />
                                    )}
                                    <div className="name">{room.name}</div>
                                    <div className="space" />
                                    {
                                        (tempRoomsOrder === undefined) ? (
                                            <IconBack className="next-icon" />
                                        ) : (
                                            <div className="up-down-icons" dir="ltr">
                                                <IconDownBoxed
                                                    className={"down " + (index === (_roomsOrder.length - 1) ? "inactive" : "")}
                                                    onClick={() => { roomTempDown(index) }} />
                                                <IconDownBoxed
                                                    className={"up " + (index === 0 ? "inactive" : "")}
                                                    onClick={() => { roomTempUp(index) }} />
                                            </div>
                                        )
                                    }
                                </div>
                            );
                        })
                    }
                    <div className="space" style={{
                        top: (_roomsOrder.length) * (75 + 8),
                        height: inRoomReordering ? (64 + 8) : 8
                    }} />
                </div>

                {
                    roomList.length === 0 ? (
                        <div className="no-room">
                            <div className="text">
                                {dataFetched ?
                                    r.notAny : s.dataIsLoading}
                            </div>
                            {
                                dataFetched ? (
                                    <div className="button" onClick={openAddRoomPages}>
                                        <div className="label">
                                            {r.add}
                                        </div>
                                        <IconPlus className="icon" />
                                    </div>
                                ) : null
                            }
                        </div>
                    ) : null
                }

                {inRoomReordering ? (
                    <div className="full-blue-button next-button" onClick={saveRoomOrder}>
                        <div>{r.rearrangeSave}</div> <IconTick className="icon" />
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export function SimplifiedRoomList({ s, dataFetched, roomsData, onClick, selectedRooms }) {
    const r = s.rooms;

    const roomList = useMemo(() => {
        const roomIds = Object.keys(roomsData.rooms);
        roomIds.sort();
        return roomIds.map(id => roomsData.rooms[id]);
    }, [roomsData.rooms]);

    return (
        <div className="room-list" style={{ height: (roomList.length) * (75 + 8) + 8 }}>
            {
                roomList.map(room => {
                    const index = roomsData.roomsOrder.indexOf(room.room_id);
                    const noColorLine = getDeviceColorsRGB(room.color) === "#000000";
                    const selected = selectedRooms.includes(room.room_id);

                    return (
                        <div
                            key={"rooms-management-cell-room-id-" + String(room.room_id)}
                            className={"room" + (selected ? " selected" : "")} style={{ top: index * (75 + 8) }}
                            onClick={() => { onClick(room) }}>

                            <RoomIcon className="icon" avatar={room.avatar} />
                            {noColorLine ? null : (
                                <div className="color-line" style={{ backgroundColor: getDeviceColorsRGB(room.color) }} />
                            )}
                            <div className="name">
                                {room.name}
                                {selected ? <IconSelection className="selection-tick" /> : null}
                            </div>
                        </div>
                    );
                })
            }
            {
                roomList.length === 0 ? (
                    <div className="empty-list-cell">
                        {dataFetched ? r.notAny : s.dataIsLoading}
                    </div>
                ) : null
            }
        </div>
    )
}